/* eslint-disable @typescript-eslint/await-thenable */

import { createModel, RematchDispatch, RematchRootState } from '@rematch/core';
import { RootModel } from '.';
import {
  IModel,
  IModelDetails,
  IModelsState,
  IModelViewDetails,
  NamedIModelDetails,
} from '../types';
import { IModelsApi } from '../apis';

const initialIModelsState: IModelsState = {
  iModel: { iModelId: '', namedVersionId: '', defaultViewId: '' } as IModel,
  iModelDetails: { name: '', instanceId: '' } as IModelDetails,
  namedIModelDetails: {
    name: '',
    instanceId: '',
    date: '',
  } as NamedIModelDetails,
  iModelViewDetails: {
    name: '',
    instanceId: '',
    thumbnail: '',
  } as IModelViewDetails,
};

export const imodels = createModel<RootModel>()({
  state: initialIModelsState,
  reducers: {
    setIModelId(state: IModelsState, payload: string): IModelsState {
      return {
        ...state,
        iModel: { ...state.iModel, iModelId: payload },
      };
    },
    setNamedVersionId(state: IModelsState, payload: string): IModelsState {
      return {
        ...state,
        iModel: { ...state.iModel, namedVersionId: payload },
      };
    },
    setDefaultViewId(state: IModelsState, payload: string): IModelsState {
      return {
        ...state,
        iModel: { ...state.iModel, defaultViewId: payload },
      };
    },
    setIModel(state: IModelsState, iModel: IModel): IModelsState {
      return {
        ...state,
        iModel,
      };
    },
    setIModelDetails(
      state: IModelsState,
      iModelDetails: IModelDetails
    ): IModelsState {
      return {
        ...state,
        iModelDetails,
      };
    },
    setNamedIModelDetails(
      state: IModelsState,
      namedIModelDetails: NamedIModelDetails
    ): IModelsState {
      return {
        ...state,
        namedIModelDetails,
      };
    },
    setIModelViewDetails(
      state: IModelsState,
      iModelViewDetails: IModelViewDetails
    ): IModelsState {
      return {
        ...state,
        iModelViewDetails,
      };
    },
  },
  effects: (dispatch: RematchDispatch<RootModel>) => ({
    async loadIModels(projectId: string): Promise<void> {
      try {
        const instances = await IModelsApi.fetchIModels(projectId);
        this.setIModels(instances);
      } catch (error) {
        dispatch.global.handleError({ error });
      }
    },

    async loadIModelFromTabData(
      _,
      rootState: RematchRootState<RootModel>
    ): Promise<void> {
      const project = rootState.projects.project;
      const tabData = rootState.tab.tabData;
      if (!tabData?.iModel || !project.name) return;

      dispatch.imodels.setIModel(tabData?.iModel ?? ({} as IModel));
      dispatch.imodels.setIModelDetails(
        await IModelsApi.fetchIModelDetails(tabData?.iModel?.iModelId ?? '')
      );
      if (tabData?.iModel?.namedVersionId !== 'latest-named-version') {
        dispatch.imodels.setNamedIModelDetails(
          (await IModelsApi.fetchNamedVersionDetails(
            tabData?.iModel?.iModelId ?? '',
            tabData?.iModel?.namedVersionId ?? ''
          )) || ({} as NamedIModelDetails)
        );
      } else {
        dispatch.imodels.setNamedIModelDetails({
          instanceId: tabData?.iModel?.namedVersionId ?? '',
          name: 'latest-named-version',
        });
      }
      if (tabData?.iModel?.defaultViewId !== '-1') {
        dispatch.imodels.setIModelViewDetails(
          await IModelsApi.fetchIModelViewDetails(
            tabData?.iModel?.iModelId ?? '',
            tabData?.iModel?.defaultViewId ?? '',
            project.id
          )
        );
      } else {
        dispatch.imodels.setIModelViewDetails({
          instanceId: tabData?.iModel?.defaultViewId ?? '',
          name: 'Default View',
        });
      }
    },
  }),
});
